// @ts-nocheck
import { useState, useRef, useEffect } from 'react';
import { Button, TextField, Icon, Tag } from '@workspace/design-system/src/components';
import { Channel, PlanType, PlanFilter } from '@src/models/channel';
import { PLAN_FILTER_ITEMS, getPlanLabel, type PlanType as ConstantsPlanType } from './constants';
import type { TagColor } from '@workspace/design-system/src/components/tag/tag';
import { STUDIO_HOST } from '@src/config';
import { formatDateTime } from '@src/utils/time';
interface ChannelTableProps {
  channels: Channel[];
  isLoading: boolean;
  searchInput: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  selectedPlan: PlanFilter;
  onPlanChange: (plan: PlanFilter) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  onChannelClick: (channel: Channel) => void;
  onApplyPlan: () => void;
}

export function ChannelTable({
  channels,
  isLoading,
  searchInput,
  onSearchChange,
  onSearch,
  selectedPlan,
  onPlanChange,
  currentPage,
  totalPages,
  onPageChange,
  onNextPage,
  onPrevPage,
  onChannelClick,
  onApplyPlan,
}: ChannelTableProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 엔터 키 입력 시 검색 실행
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePlanFilter = (newPlan: PlanFilter) => {
    onPlanChange(newPlan);
    setIsDropdownOpen(false);
  };

  const getPlanFilterLabel = () => {
    const item = PLAN_FILTER_ITEMS.find((item) => item.value === selectedPlan);
    return item?.label || '전체 보기';
  };

  // 요금제 배지 색상 매핑
  const getPlanTagColor = (plan?: PlanType): TagColor => {
    if (!plan) return 'gray';
    const normalizedPlan = plan.toLowerCase() as ConstantsPlanType;
    if (normalizedPlan === 'free') return 'gray';
    if (normalizedPlan === 'realworld') return 'black';
    return 'green'; // pro, enterprise, teacher, class
  };

  const getPlanBadge = (plan?: PlanType) => {
    if (!plan) {
      return (
        <Tag
          type="default"
          color="gray"
          size="S-16"
          text="없음"
        />
      );
    }
    const normalizedPlan = plan.toLowerCase() as ConstantsPlanType;
    const label = getPlanLabel(normalizedPlan);
    const color = getPlanTagColor(normalizedPlan);
    
    return (
      <Tag
        type="default"
        color={color}
        size="S-16"
        text={label}
      />
    );
  };

  return (
    <div className="bg-white rounded-[24px] p-10 shadow-sm">
      {/* 검색 및 필터 */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-[400px]">
          <div className="flex-1">
            <TextField
              placeholder="채널명 또는 채널 ID를 검색해 주세요"
              value={searchInput}
              onChange={onSearchChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          <Button styleType="text-primary" styleSize="S-32" onClick={onSearch}>
            검색
          </Button>
        </div>

        <div className="relative w-44" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full h-10 px-4 bg-[#F5F5F5] rounded-lg flex items-center justify-between text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] hover:bg-[#ECECEC] transition-colors"
          >
            <span>{getPlanFilterLabel()}</span>
            <Icon name="Down" size={16} color="#101010" />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-44 bg-white rounded-2xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)] py-2 z-10">
              {PLAN_FILTER_ITEMS.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handlePlanFilter(item.value)}
                  className="w-full px-3 py-2 text-left text-[14px] hover:bg-[#F5F5F5] flex items-center justify-between transition-colors"
                >
                  <span className={selectedPlan === item.value ? 'text-[#3C78F4]' : 'text-[#101010]'}>
                    {item.label}
                  </span>
                  <div className="flex items-center justify-center">
                    {selectedPlan === item.value ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="8" cy="8" r="7" fill="#3C78F4" stroke="#3C78F4" strokeWidth="1" />
                        <path
                          d="M5.5 8L7 9.5L10.5 6"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="8" cy="8" r="7" fill="#F0F0F0" strokeWidth="1" />
                        <path
                          d="M5.5 8L7 9.5L10.5 6"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 테이블 */}
      <div className="bg-white overflow-hidden">
        {/* 테이블 헤더 */}
        <div className="bg-[#F0F0F0] rounded-t-lg">
          <div className="grid grid-cols-6 gap-4 px-4 py-3">
            <div className="text-[14px] font-bold text-[#101010] leading-[16px]">채널명</div>
            <div className="text-[14px] font-bold text-[#101010] leading-[16px]">채널 ID</div>
            <div className="text-[14px] font-bold text-[#101010] leading-[16px]">생성일</div>
            <div className="text-[14px] font-bold text-[#101010] leading-[16px]">콘텐츠</div>
            <div className="text-[14px] font-bold text-[#101010] leading-[16px]">요금제</div>
            <div className="text-[14px] font-bold text-[#101010] leading-[16px]">바로가기</div>
          </div>
        </div>

        {/* 테이블 바디 */}
        <div className="min-h-[400px]">
          {isLoading ? (
            // 로딩 상태
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-[#F0F0F0] border-t-[#3C78F4] rounded-full animate-spin" />
                <p className="text-[14px] text-[#595959]">로딩 중...</p>
              </div>
            </div>
          ) : channels.length === 0 ? (
            // 빈 상태
            <div className="flex items-center justify-center py-20 border-t border-[#F0F0F0]">
              <p className="text-[14px] text-[#BFBFBF]">등록된 채널이 없어요</p>
            </div>
          ) : (
            // 데이터 표시
            channels.map((item, index) => (
              <div
                key={item.channelId || `channel-${index}`}
                className="grid grid-cols-6 gap-4 px-4 py-3 hover:bg-[#F5F5F5] border-t border-[#F0F0F0] transition-colors"
              >
                <button
                  className="text-[14px] text-[#3C78F4] font-bold underline leading-[16px] text-left cursor-pointer"
                  onClick={() => onChannelClick(item)}
                >
                  {item.channelName}
                </button>
                <div className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] select-text">
                  {item.channelId}
                </div>
                <div className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] select-text">
                  {formatDateTime(item.createdAt)}
                </div>
                <div className="text-[14px] leading-[20px] tracking-[-0.1px]">
                  {typeof item.contentCount === 'number' ? (
                    <a
                      href={`/creator/channel/${item.channelId}/project`}
                      className="text-[#3C78F4] font-bold underline cursor-pointer hover:text-[#2557C5]"
                    >
                      {item.contentCount.toLocaleString()}
                    </a>
                  ) : (
                    '-'
                  )}
                </div>
                <div className="flex items-center">{getPlanBadge(item.plan)}</div>
                <div className="flex items-center">
                  <Button
                    styleType="basic"
                    styleSize="S-32"
                    onClick={() => window.open(`https://${STUDIO_HOST}/apps/${item.channelId}`, '_blank')}
                  >
                    스튜디오
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 페이지네이션 */}
      {!isLoading && channels.length > 0 && (
        <div className="flex items-center justify-center gap-4 mt-6 py-4">
          <button
            onClick={onPrevPage}
            disabled={currentPage === 1}
            className="p-2 text-[#595959] hover:text-[#101010] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Icon name="ChevronLeft" size={16} color={currentPage === 1 ? '#BFBFBF' : '#595959'} />
          </button>

          {(() => {
            const pages: (number | string)[] = [];
            const siblingCount = 2; // 현재 페이지 양옆에 보여줄 페이지 수
            const totalPageNumbers = siblingCount * 2 + 1; // 현재 페이지 포함 총 5개

            // 총 페이지가 충분히 적으면 모두 표시 (1 + 5 + 1 + 1 = 8개)
            if (totalPages <= totalPageNumbers + 2) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              // 항상 첫 페이지 표시
              pages.push(1);

              // 현재 페이지 기준으로 범위 계산 (±2)
              let startPage = Math.max(2, currentPage - siblingCount);
              let endPage = Math.min(totalPages - 1, currentPage + siblingCount);

              // 왼쪽 끝에 있을 때 오른쪽으로 확장
              if (currentPage <= siblingCount + 1) {
                endPage = Math.min(totalPageNumbers, totalPages - 1);
              }

              // 오른쪽 끝에 있을 때 왼쪽으로 확장
              if (currentPage >= totalPages - siblingCount) {
                startPage = Math.max(2, totalPages - totalPageNumbers);
              }

              // 왼쪽 ... 표시 여부
              const showLeftEllipsis = startPage > 2;
              if (showLeftEllipsis) {
                pages.push('left-ellipsis');
              }

              // 중간 페이지들 표시
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }

              // 오른쪽 ... 표시 여부
              const showRightEllipsis = endPage < totalPages - 1;
              if (showRightEllipsis) {
                pages.push('right-ellipsis');
              }

              // 항상 마지막 페이지 표시
              pages.push(totalPages);
            }

            return pages.map((page) => {
              if (typeof page === 'string') {
                // ... (ellipsis) 표시
                return (
                  <div key={page} className="flex items-center justify-center w-6 h-6">
                    <Icon name="Meatball" size={24} color="#595959" />
                  </div>
                );
              }

              const isActive = page === currentPage;
              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`w-6 h-6 rounded text-[14px] font-bold leading-[16px] ${isActive ? 'bg-[#E6F4FF] text-[#3C78F4]' : 'text-[#595959] hover:text-[#101010]'
                    } transition-colors`}
                >
                  {page}
                </button>
              );
            });
          })()}

          <button
            onClick={onNextPage}
            disabled={currentPage === totalPages}
            className="p-2 text-[#595959] hover:text-[#101010] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Icon
              name="ChevronRight"
              size={16}
              color={currentPage === totalPages ? '#BFBFBF' : '#595959'}
            />
          </button>
        </div>
      )}
    </div>
  );
}
